import { Migration } from '@gleamer/types';
import React, { useEffect, useState } from 'react';
import { useMigrationErrorGroups, useMigrationErrors } from '../../../../hooks/useMigration';
import { PageButton } from '../../../atoms/PageButton';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { formatDateTime } from '../imports/format';
import { CommandButton, formatCount, selectClassName } from './parts';

type MigrationRun = Migration.MigrationRun;
type MigrationErrorGroup = Migration.MigrationErrorGroup;
type MigrationErrorSelection = Migration.MigrationErrorSelection;

export const MIGRATION_ERRORS_PAGE_SIZE = 50;

/** Which errors the section shows: the operator's work (unacknowledged), what was accepted, or both. */
type Shown = 'false' | 'true' | '';

/**
 * Which errors of an opened group the operator picked: every one but those unticked (`all`, the default — the group's
 * criteria less some ids, however many rows it has), or only those ticked (`only`).
 */
type Picked = { mode: 'all' | 'only'; ids: number[] };

const EVERY_ONE: Picked = { mode: 'all', ids: [] };

/** The rows and entities of a group the filter shows. */
function counted(group: MigrationErrorGroup, shown: Shown) {
  if (shown === 'false') {
    return { errors: group.unacknowledged, entities: group.unacknowledgedOccurrences };
  }
  if (shown === 'true') {
    return {
      errors: group.errors - group.unacknowledged,
      entities: group.occurrences - group.unacknowledgedOccurrences,
    };
  }
  return { errors: group.errors, entities: group.occurrences };
}

/** The groups by kind, in the order viewer-api gives them (most unacknowledged entities first). */
function byKind(groups: MigrationErrorGroup[]): [string, MigrationErrorGroup[]][] {
  const kinds = new Map<string, MigrationErrorGroup[]>();
  groups.forEach(group => kinds.set(group.kind, [...(kinds.get(group.kind) ?? []), group]));
  return Array.from(kinds.entries());
}

const errorCount = (count: number) => `${formatCount(count)} error${count === 1 ? '' : 's'}`;
const entityCount = (count: number) => `${formatCount(count)} entit${count === 1 ? 'y' : 'ies'}`;

/**
 * Acknowledge and rerun, side by side, for one set of errors: acknowledging accepts them as not migrated, rerunning
 * reads their entities again — the segments that recorded them, whose other errors come back if still there.
 */
function ErrorActions({
  run,
  canRerun,
  errors,
  count,
  name,
  all = false,
  label: labelled = '',
  onSent,
}: {
  run: MigrationRun;
  canRerun: boolean;
  errors: MigrationErrorSelection;
  count: number;
  name: string;
  /** Whether the buttons say `all`: a kind's, or the run's. */
  all?: boolean;
  /** What the buttons name after the count: the kind, for a kind's. */
  label?: string;
  onSent?: () => void;
}) {
  if (count === 0) {
    return null;
  }
  const what = `${errorCount(count)} of ${name}`;
  const label = (verb: string) =>
    [verb, all ? 'all' : '', formatCount(count), labelled].filter(Boolean).join(' ');
  return (
    <>
      <CommandButton
        label={label('Acknowledge')}
        confirm={`Accept ${what} as not migrated?`}
        command={{ type: 'ACKNOWLEDGE', runId: run.id, errors }}
        onSent={onSent}
      />
      {canRerun && (
        <CommandButton
          label={label('Rerun')}
          confirm={`Read the entities of ${what} again? Their segments are replayed: what is still wrong is reported again.`}
          command={{ type: 'RERUN', runId: run.id, errors }}
          onSent={onSent}
        />
      )}
    </>
  );
}

/**
 * A run's errors: what was recorded and skipped — a dangling reference, a file gone from a bucket. They come by kind,
 * then by reason, so a common cause shows as one line however many entities it hits. A group is its criteria, not a
 * list: an operator acknowledges it or reruns it whole, less the rows they untick, or only rows they pick. A bulk
 * run is `DONE_WITH_ERRORS` until none is left unacknowledged.
 */
export function MigrationErrorsSection({
  run,
  canRerun,
}: {
  run: MigrationRun;
  canRerun: boolean;
}) {
  const [shown, setShown] = useState<Shown>('false');
  const { data: groups, status, error } = useMigrationErrorGroups(String(run.id));
  const visible = (groups ?? []).filter(group => counted(group, shown).errors > 0);

  return (
    <section
      aria-labelledby="errors-title"
      className="mb-4"
    >
      <div className="mb-2 flex flex-wrap items-end gap-4">
        <h3
          id="errors-title"
          className="mr-auto text-xl font-bold"
        >
          Errors ({formatCount(run.unacknowledgedErrors)} unacknowledged)
        </h3>
        <label className="flex flex-col text-sm">
          Show
          <select
            className={selectClassName}
            value={shown}
            onChange={e => setShown(e.target.value as Shown)}
          >
            <option value="false">Unacknowledged</option>
            <option value="true">Acknowledged</option>
            <option value="">All</option>
          </select>
        </label>
        <span className="inline-flex flex-wrap items-center gap-2">
          <ErrorActions
            run={run}
            canRerun={canRerun}
            errors={{}}
            count={run.unacknowledgedErrors}
            name="every kind"
            all
          />
        </span>
      </div>

      {status === 'loading' && <div>Loading…</div>}
      {status === 'error' && (
        <div
          role="alert"
          className="text-dl-danger"
        >
          Could not load the errors: {(error as Error)?.message}
        </div>
      )}
      {status === 'success' && visible.length === 0 && <div>No error.</div>}
      {status === 'success' &&
        byKind(visible).map(([kind, reasons]) => (
          <KindErrors
            key={kind}
            run={run}
            canRerun={canRerun}
            kind={kind}
            groups={reasons}
            shown={shown}
          />
        ))}
    </section>
  );
}

/** One kind's errors, a line per reason; the kind's actions take all of its unacknowledged errors at once. */
function KindErrors({
  run,
  canRerun,
  kind,
  groups,
  shown,
}: {
  run: MigrationRun;
  canRerun: boolean;
  kind: string;
  groups: MigrationErrorGroup[];
  shown: Shown;
}) {
  const unacknowledged = groups.reduce((sum, group) => sum + group.unacknowledged, 0);
  const errors = groups.reduce((sum, group) => sum + counted(group, shown).errors, 0);
  const entities = groups.reduce((sum, group) => sum + counted(group, shown).entities, 0);
  const titleId = `errors-kind-${kind}`;

  return (
    <section
      aria-labelledby={titleId}
      className="mb-3 rounded-md border border-dl-border"
    >
      <div className="flex flex-wrap items-center gap-3 bg-dl-sunken px-3 py-2">
        <h4
          id={titleId}
          className="text-lg font-bold"
        >
          {kind}
        </h4>
        <span className="mr-auto text-sm text-dl-text">
          {errorCount(errors)} · {entityCount(entities)}
        </span>
        {shown !== 'true' && (
          <span className="inline-flex flex-wrap items-center gap-2">
            <ErrorActions
              run={run}
              canRerun={canRerun}
              errors={{ kind }}
              count={unacknowledged}
              name={`every ${kind} reason`}
              all
              label={kind}
            />
          </span>
        )}
      </div>
      <table className="w-full table-auto">
        <thead className="bg-dl-track">
          <tr>
            <TableHeadTh>Reason</TableHeadTh>
            <TableHeadTh>Errors</TableHeadTh>
            <TableHeadTh>Entities</TableHeadTh>
            <TableHeadTh>Last recorded</TableHeadTh>
            <TableHeadTh>Actions</TableHeadTh>
          </tr>
        </thead>
        <tbody>
          {groups.map(group => (
            <ReasonErrors
              key={group.reason}
              run={run}
              canRerun={canRerun}
              group={group}
              shown={shown}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * One kind and reason: its counts and, opened, its rows. Its actions take what is picked among the rows — the whole
 * group, as criteria, until rows are unticked.
 */
function ReasonErrors({
  run,
  canRerun,
  group,
  shown,
}: {
  run: MigrationRun;
  canRerun: boolean;
  group: MigrationErrorGroup;
  shown: Shown;
}) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Picked>(EVERY_ONE);
  const { errors, entities } = counted(group, shown);
  const name = `${group.kind} ${group.reason}`;

  // another filter shows other rows: start again from the whole group
  useEffect(() => setPicked(EVERY_ONE), [shown]);

  const selection: MigrationErrorSelection =
    picked.mode === 'all'
      ? { kind: group.kind, reason: group.reason, excludedIds: picked.ids }
      : { errorIds: picked.ids };
  const count =
    picked.mode === 'all'
      ? Math.max(0, group.unacknowledged - picked.ids.length)
      : picked.ids.length;

  return (
    <>
      <tr>
        <TableBodyTd>
          <span
            className="font-mono text-sm font-bold"
            title={Migration.MIGRATION_ERROR_REASONS[group.reason]}
          >
            {group.reason}
          </span>
        </TableBodyTd>
        <TableBodyTd>
          {formatCount(errors)}
          {shown === '' && group.unacknowledged > 0 && (
            <span className="ml-1 text-sm text-dl-muted">
              ({formatCount(group.unacknowledged)} unacknowledged)
            </span>
          )}
        </TableBodyTd>
        <TableBodyTd>{formatCount(entities)}</TableBodyTd>
        <TableBodyTd>{formatDateTime(group.lastRecordedAt)}</TableBodyTd>
        <TableBodyTd>
          <span className="inline-flex flex-wrap items-center gap-2">
            <PageButton
              aria-expanded={open}
              aria-label={`${open ? 'Hide' : 'Show'} the ${name} errors`}
              onClick={() => setOpen(o => !o)}
            >
              {open ? 'Hide errors' : 'Show errors'}
            </PageButton>
            {shown !== 'true' && (
              <ErrorActions
                run={run}
                canRerun={canRerun}
                errors={selection}
                count={count}
                name={name}
                onSent={() => setPicked(EVERY_ONE)}
              />
            )}
          </span>
        </TableBodyTd>
      </tr>
      {open && (
        <tr>
          <td
            colSpan={5}
            className="bg-dl-raised px-3 pb-3"
          >
            <ErrorRows
              run={run}
              group={group}
              shown={shown}
              picked={picked}
              onPick={setPicked}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/** A group's rows, page by page, most recent first, each unacknowledged one ticked while it is in what is picked. */
function ErrorRows({
  run,
  group,
  shown,
  picked,
  onPick,
}: {
  run: MigrationRun;
  group: MigrationErrorGroup;
  shown: Shown;
  picked: Picked;
  onPick: (picked: Picked) => void;
}) {
  const [page, setPage] = useState(0);
  const { data, status, error } = useMigrationErrors(String(run.id), {
    page,
    size: MIGRATION_ERRORS_PAGE_SIZE,
    kind: group.kind,
    reason: group.reason,
    acknowledged: shown === '' ? undefined : shown === 'true',
  });

  // the filter changed: another set of rows, from its first page
  useEffect(() => setPage(0), [shown]);

  // an acknowledge or a rerun can empty the page shown (the default filter is what they clear): go back to the last
  // page that has rows, or the pager would be gone with it
  useEffect(() => {
    if (status === 'success' && data.items.length === 0 && page > 0) {
      setPage(Math.max(0, Math.min(page - 1, data.pages - 1)));
    }
  }, [status, data, page]);

  const isPicked = (id: number) => (picked.mode === 'all') !== picked.ids.includes(id);
  // ticking a row puts it in what is picked: out of the exclusions, or into the chosen ids
  const tick = (ids: number[], on: boolean) => {
    const listed = (picked.mode === 'all') !== on;
    onPick({
      mode: picked.mode,
      ids: listed
        ? Array.from(new Set([...picked.ids, ...ids]))
        : picked.ids.filter(id => !ids.includes(id)),
    });
  };
  const unacknowledgedOnPage = data?.items.filter(e => !e.acknowledgedAt).map(e => e.id) ?? [];

  if (status === 'loading') {
    return <div className="py-2">Loading…</div>;
  }
  if (status === 'error') {
    return (
      <div
        role="alert"
        className="py-2 text-dl-danger"
      >
        Could not load the errors: {(error as Error)?.message}
      </div>
    );
  }
  if (data.items.length === 0) {
    return <div className="py-2">No error.</div>;
  }
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 py-2 text-sm">
        <span>
          {picked.mode === 'all'
            ? picked.ids.length === 0
              ? 'The whole group is picked.'
              : `The whole group but ${picked.ids.length} unticked.`
            : `${picked.ids.length} picked.`}
        </span>
        {picked.mode === 'all' ? (
          <PageButton onClick={() => onPick({ mode: 'only', ids: [] })}>Select none</PageButton>
        ) : (
          <PageButton onClick={() => onPick(EVERY_ONE)}>Select the whole group</PageButton>
        )}
      </div>
      <table className="w-full table-auto">
        <thead>
          <tr>
            <TableHeadTh>
              <input
                type="checkbox"
                aria-label="Pick the unacknowledged errors of this page"
                disabled={unacknowledgedOnPage.length === 0}
                checked={unacknowledgedOnPage.length > 0 && unacknowledgedOnPage.every(isPicked)}
                onChange={e => tick(unacknowledgedOnPage, e.target.checked)}
              />
            </TableHeadTh>
            <TableHeadTh>Entity</TableHeadTh>
            <TableHeadTh>Detail</TableHeadTh>
            <TableHeadTh>Segment</TableHeadTh>
            <TableHeadTh>Recorded</TableHeadTh>
            <TableHeadTh>Acknowledged</TableHeadTh>
          </tr>
        </thead>
        <tbody>
          {data.items.map(e => (
            <tr key={e.id}>
              <TableBodyTd>
                {!e.acknowledgedAt && (
                  <input
                    type="checkbox"
                    aria-label={`Pick error ${e.id}`}
                    checked={isPicked(e.id)}
                    onChange={ev => tick([e.id], ev.target.checked)}
                  />
                )}
              </TableBodyTd>
              <TableBodyTd className="break-all font-mono text-sm">
                {e.entityKey}
                {e.occurrences > 1 && (
                  <span className="ml-2 font-sans text-dl-muted">
                    × {e.occurrences.toLocaleString()}
                  </span>
                )}
              </TableBodyTd>
              <TableBodyTd className="text-sm text-dl-text">{e.detail ?? '—'}</TableBodyTd>
              <TableBodyTd>{e.segmentId ?? '—'}</TableBodyTd>
              <TableBodyTd>{formatDateTime(e.createdAt)}</TableBodyTd>
              <TableBodyTd title={e.acknowledgedBy ?? undefined}>
                {e.acknowledgedAt
                  ? `${formatDateTime(e.acknowledgedAt)} by ${e.acknowledgedBy}`
                  : '—'}
              </TableBodyTd>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-2 py-2">
        <span>
          Page {data.page + 1} of {Math.max(1, data.pages)} ({data.total} errors)
        </span>
        <PageButton
          onClick={() => setPage(p => Math.max(0, p - 1))}
          disabled={page === 0}
        >
          Previous page
        </PageButton>
        <PageButton
          onClick={() => setPage(p => p + 1)}
          disabled={page + 1 >= data.pages}
        >
          Next page
        </PageButton>
      </div>
    </>
  );
}
